import { ErrorHandler } from "@/web/lib/errors";

export const DARKEST_COLOR = "#111111";
export const LIGHTEST_COLOR = "#f5f5f5";

function normalizeHex(hex: string): string {
  let hexValue = hex.replace(/^#/, "");
  if (hexValue.length === 3) {
    hexValue = hexValue
      .split("")
      .map((c) => c + c)
      .join("");
  }
  return hexValue;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const normalized = normalizeHex(hex);
  return {
    r: Number.parseInt(normalized.slice(0, 2), 16),
    g: Number.parseInt(normalized.slice(2, 4), 16),
    b: Number.parseInt(normalized.slice(4, 6), 16),
  };
}

function getBrightness(color: { r: number; g: number; b: number }): number {
  return 0.299 * color.r + 0.587 * color.g + 0.114 * color.b;
}

export function hexToHsl(hex: string): [number, number, number] {
  const normalized = normalizeHex(hex);
  const r = Number.parseInt(normalized.slice(0, 2), 16) / 255;
  const g = Number.parseInt(normalized.slice(2, 4), 16) / 255;
  const b = Number.parseInt(normalized.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h = h / 6;
  }

  return [h * 360, s, l];
}

export function getTextColor(hex: string, overrideThreshold?: number): string {
  const [_h, _s, l] = hexToHsl(hex);
  return l > (overrideThreshold ?? 0.8) ? "#000000" : "#FFFFFF";
}

export type DominantColorsOptions = {
  count?: number;
  /** Minimum RGB distance between returned colors (0–441). ~50 dedupes near-duplicates. */
  minDistance?: number;
  /** Drop bucket candidates whose HSL lightness exceeds this (0–1). */
  maxLightness?: number;
  /** Drop bucket candidates whose HSL lightness is below this (0–1). */
  minLightness?: number;
};

/**
 * Extracts the top dominant colors from `imageUrl` by drawing the image to an
 * offscreen canvas, bucketing pixels into a 3D RGB grid, then ranking buckets
 * by frequency × saturation and deduping near-duplicates by perceptual distance.
 *
 * Accepts either http(s) URLs (requires CORS; Google Places media URLs allow
 * anonymous cross-origin) or `data:` URLs. Returns at most `count` hex colors;
 * falls back to a neutral set on error or if the canvas is tainted.
 */
export async function getDominantColorsHex(
  imageUrl: string,
  options: DominantColorsOptions = {},
): Promise<string[]> {
  const count = options.count ?? 4;
  if (typeof window === "undefined" || !imageUrl) return FALLBACK_COLORS.slice(0, count);

  try {
    const image = await loadImageForCanvas(imageUrl);
    const { canvas, ctx } = drawScaled(image, 64);
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const palette = bucketDominantColors(data, { ...options, count });
    return palette.length > 0 ? palette : FALLBACK_COLORS.slice(0, count);
  } catch (error: unknown) {
    ErrorHandler.logError(error);
    return FALLBACK_COLORS.slice(0, count);
  }
}

const FALLBACK_COLORS = ["#2D3B6E", "#6E2D2D", "#2D6E3B", "#6E6E2D"];

function loadImageForCanvas(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (!src.startsWith("data:")) img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image for color extraction: ${src}`));
    img.src = src;
  });
}

function drawScaled(
  image: HTMLImageElement,
  maxDim: number,
): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const scale = Math.min(1, maxDim / Math.max(image.naturalWidth, image.naturalHeight));
  const w = Math.max(1, Math.round(image.naturalWidth * scale));
  const h = Math.max(1, Math.round(image.naturalHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("2D canvas context unavailable");
  ctx.drawImage(image, 0, 0, w, h);
  return { canvas, ctx };
}

/**
 * Buckets pixels into an 8x8x8 RGB grid, discards near-white/near-black buckets,
 * scores each bucket by frequency weighted by saturation, then greedily picks
 * the top `count` while enforcing a minimum perceptual distance between picks.
 *
 * `maxLightness` / `minLightness` (when provided) clip candidates whose average
 * HSL lightness falls outside the range — used here to prefer dark colors that
 * read under white text.
 */
function bucketDominantColors(
  data: Uint8ClampedArray,
  options: { count: number; minDistance?: number; maxLightness?: number; minLightness?: number },
): string[] {
  const { count, minDistance = 0, maxLightness, minLightness } = options;
  const BUCKETS_PER_AXIS = 8;
  const STEP = 256 / BUCKETS_PER_AXIS;
  const sums = new Map<number, { r: number; g: number; b: number; n: number }>();

  for (let i = 0; i < data.length; i += 4) {
    const alpha = data[i + 3];
    if (alpha === undefined || alpha < 128) continue;
    const r = data[i] ?? 0;
    const g = data[i + 1] ?? 0;
    const b = data[i + 2] ?? 0;

    const brightness = 0.299 * r + 0.587 * g + 0.114 * b;
    if (brightness < 16 || brightness > 240) continue;

    const bucket =
      Math.floor(r / STEP) * BUCKETS_PER_AXIS * BUCKETS_PER_AXIS +
      Math.floor(g / STEP) * BUCKETS_PER_AXIS +
      Math.floor(b / STEP);

    const entry = sums.get(bucket);
    if (entry) {
      entry.r += r;
      entry.g += g;
      entry.b += b;
      entry.n += 1;
    } else {
      sums.set(bucket, { r, g, b, n: 1 });
    }
  }

  type Candidate = { r: number; g: number; b: number; score: number };
  const candidates: Candidate[] = [];
  for (const { r, g, b, n } of sums.values()) {
    const ar = Math.round(r / n);
    const ag = Math.round(g / n);
    const ab = Math.round(b / n);
    const [, s, l] = rgbToHsl(ar, ag, ab);
    if (maxLightness !== undefined && l > maxLightness) continue;
    if (minLightness !== undefined && l < minLightness) continue;
    // Boost saturated colors so muddy greys lose to vivid ones with similar counts.
    const score = n * (0.4 + s);
    candidates.push({ r: ar, g: ag, b: ab, score });
  }

  candidates.sort((a, b) => b.score - a.score);

  const picked: Candidate[] = [];
  for (const c of candidates) {
    if (picked.length >= count) break;
    const tooClose = picked.some((p) => rgbDistance(p, c) < minDistance);
    if (tooClose) continue;
    picked.push(c);
  }

  return picked.map(({ r, g, b }) => rgbToHex(r, g, b));
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn:
        h = (gn - bn) / d + (gn < bn ? 6 : 0);
        break;
      case gn:
        h = (bn - rn) / d + 2;
        break;
      case bn:
        h = (rn - gn) / d + 4;
        break;
    }
    h /= 6;
  }
  return [h * 360, s, l];
}

function rgbDistance(
  a: { r: number; g: number; b: number },
  b: { r: number; g: number; b: number },
): number {
  const dr = a.r - b.r;
  const dg = a.g - b.g;
  const db = a.b - b.b;
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}

export function isLighterThanOrEqualToThreshold(hex: string, minThreshold: string): boolean {
  const rgb = hexToRgb(hex);
  const value = getBrightness(rgb);
  const minRgb = hexToRgb(minThreshold);
  const minValue = getBrightness(minRgb);
  return minValue <= value;
}
